import React, { lazy, Suspense, useContext, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import Layout from "../src/components/common/Layout";
import ProtectedRoute from "../src/components/common/ProtectedRoute";
import { AuthContext, LoaderProvider } from "./Context/ContextFunctions";

// Lazy-loaded pages
const Dashboard = lazy(() => import("./Pages/Dashboard/Dashboard"));
const Logout = lazy(() => import("./Pages/Login/Login"));
const ClientList = lazy(() => import("./Pages/Client/ClientList"));
const ViewClient = lazy(() => import("./Pages/Client/ViewClient"));
const ViewStaff = lazy(() => import("./Pages/Staff/ViewStaffDetails"));
const Login = lazy(() => import("./Pages/Login/Login"));
const JobList = lazy(() => import("./Pages/Job/JobList"));
const StaffList = lazy(() => import("./Pages/Staff/StaffList"));
const SettingList = lazy(() => import("./Pages/Setting/SettingList"));
const ReportList = lazy(() => import("./Pages/Report/ReportList"));
const RepprtLedger = lazy(() => import("./Pages/Report/ClientLedger"));
const TaskViewLog = lazy(() => import("./Pages/Report/TaskViewLog"));

const AddUpdateClient = lazy(() => import("./Pages/Client/AddUpdateClient"));
const AddUpdateJob = lazy(() => import("./Pages/Job/AddUpdateJob"));
const TaskSheetList = lazy(() => import("./Pages/Job/TaskSheetList"));

const NotificationList = lazy(() =>
  import("./Pages/Notification/Notification")
);
const ViewLog = lazy(() => import("./Pages/Staff/ViewLog"));
const ServiceJobType = lazy(() =>
  import("./Pages/Setting/ServiceJobType/ServiceJobTypeList")
);
const AddUpdateChecklist = lazy(() =>
  import("./Pages/Setting/Checklist/AddUpdateChecklist")
);
const SubscriptionsList = lazy(() =>
  import("./Pages/Subscriptions/SubscriptionList")
);
const Checkoutlist = lazy(() => import("./components/common/CheckOutList"));
const SubscriptionGraph = lazy(() => import("./components/common/GraphModue"));
const VisitorsList = lazy(() => import("./Pages/Visitors/VisitorsList"));
const PaperWork = lazy(() => import("./components/common/PaperWork"));
const Campaign = lazy(() => import("./Pages/Campaign/Campaign"));
const LeaveList = lazy(() => import("./Pages/LeaveManage/LeaveManagementList"));
const ProfileViewDetails = lazy(() =>
  import("./Pages/Profile/ProfileViewDetails")
);
const ActivityLogPage = lazy(() => import("./components/common/ActivityLog"));
const KnowledgeBase = lazy(() => import("./Pages/KnowledgeBase/KnowledgeBase"));
const ReminderList = lazy(() => import("./Pages/Reminder/ReminderList"));
const EmailTemplateList = lazy(() =>
  import("./Pages/EmailTemplate/EmailTemplateList")
);
const TemplatePage = lazy(() =>
  import("./Pages/EmailTemplate/AddUpdateEmailTemplate")
);
const SupportList = lazy(() => import("./Pages/Support/SupportsList"));
const CampaignList = lazy(() => import("./Pages/Campaign/CampaignList"));

const App = () => {
  // Mock permissions list (replace with your actual logic from Redux, Context, etc.)

  const { defaultAccess } = useContext(AuthContext);

  if (!defaultAccess) {
    return (
      <div className="flex items-center justify-center h-screen">
        Loading...
      </div>
    ); // Or a spinner
  }
  console.log("defaultAccess", defaultAccess);
  return (
    <Router>
      <Layout>
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-screen">
              Loading...
            </div>
          }
        >
          <LoaderProvider>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute permission={true} element={Dashboard} />
                }
              />
              <Route path="/login" element={<Login />} />
              <Route path="/logout" element={<Logout />} />
              <Route
                path="/client"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ClientCanView}
                    element={ClientList}
                  />
                }
              />
              <Route
                path="/addUpdateClient"
                element={
                  <ProtectedRoute
                    permission={
                      defaultAccess.ClientCanView ||
                      (defaultAccess.ClientCanUpdate &&
                        defaultAccess.ClientCanInsert)
                    }
                    element={AddUpdateClient}
                  />
                }
              />
              <Route
                path="/viewClient"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ClientCanView}
                    element={ViewClient}
                  />
                }
              />
              <Route
                path="/job"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.JobCanView}
                    element={JobList}
                  />
                }
              />
              <Route
                path="/addUpdateJob"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.JobCanView}
                    element={AddUpdateJob}
                  />
                }
              />
              <Route
                path="/TaskSheetList"
                element={
                  <ProtectedRoute permission={true} element={TaskSheetList} />
                }
              />
              <Route
                path="/TaskViewLog"
                element={
                  <ProtectedRoute permission={true} element={TaskViewLog} />
                }
              />
              <Route
                path="/viewStaff"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.UserCanView}
                    element={ViewStaff}
                  />
                }
              />
              <Route
                path="/viewLog"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.UserCanView}
                    element={ViewLog}
                  />
                }
              />
              <Route
                path="/staff"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.UserCanView}
                    element={StaffList}
                  />
                }
              />
              <Route
                path="/report"
                element={
                  <ProtectedRoute permission={defaultAccess.ClientReportsCanView} element={ReportList} />
                }
              />
              <Route
                path="/ClientLedger"
                element={
                  <ProtectedRoute permission={true} element={RepprtLedger} />
                }
              />
              <Route
                path="/setting"
                element={
                  <ProtectedRoute permission={defaultAccess?.Config} element={SettingList} />
                }
              />
              <Route
                path="/notification"
                element={
                  <ProtectedRoute
                    permission={true}
                    element={NotificationList}
                  />
                }
              />
              <Route
                path="/service-job-type"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ConfigureServicesCanView}
                    element={ServiceJobType}
                  />
                }
              />
              <Route
                path="/addupdateChecklist"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ConfigureCheckListCanView}
                    element={AddUpdateChecklist}
                  />
                }
              />
              <Route
                path="/subscriptions"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.SubscriptionCanView}
                    element={SubscriptionsList}
                  />
                }
              />
              <Route
                path="/viewCheckInLog"
                element={
                  <ProtectedRoute permission={true} element={Checkoutlist} />
                }
              />
              <Route
                path="/Usage"
                element={
                  <ProtectedRoute
                    permission={true}
                    element={SubscriptionGraph}
                  />
                }
              />
              <Route
                path="/visitors"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.VisitorCanView}
                    element={VisitorsList}
                  />
                }
              />
              <Route
                path="/paperWork"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.DocumentManagerCanView}
                    element={PaperWork}
                  />
                }
              />
              <Route
                path="/addUpdateCampaign"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.CampaignCanView}
                    element={Campaign}
                  />
                }
              />
              <Route
                path="/campaign"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.CampaignCanView}
                    element={CampaignList}
                  />
                }
              />
              <Route
                path="/LeaveManagement"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.LeaveCanView}
                    element={LeaveList}
                  />
                }
              />
              <Route
                path="/profile-details"
                element={
                  <ProtectedRoute
                    permission={true}
                    element={ProfileViewDetails}
                  />
                }
              />
              <Route
                path="/activity"
                element={
                  <ProtectedRoute permission={true} element={ActivityLogPage} />
                }
              />
              <Route
                path="/knowledge-base"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.KnowledgeBaseCanView}
                    element={KnowledgeBase}
                  />
                }
              />
              <Route
                path="/reminder"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ReminderCanView}
                    element={ReminderList}
                  />
                }
              />
              <Route
                path="/emailTemplate"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ConfigureEmailTemplatesCanView}
                    element={EmailTemplateList}
                  />
                }
              />
              <Route
                path="/addUpdateTemplate"
                element={
                  <ProtectedRoute
                    permission={defaultAccess.ConfigureEmailTemplatesCanView}
                    element={TemplatePage}
                  />
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute permission={true} element={SupportList} />
                }
              />
            </Routes>
          </LoaderProvider>
        </Suspense>
      </Layout>
    </Router>
  );
};

export default App;
